import { DevelopmentError } from '../other/development-error';
import { errorList, IErrorList } from '../other/error-list';
import { isNothing, isSomething } from '../other/general/nothing';
import { LangPricingModel } from '../../localization/webmodule-common/pricing/pricing-model-lang';

export const pricingModelType: { [key: string]: PricingModelType } = {
  margin: 1,
  markup: 2
};

export type PricingModelType = 1 | 2;

export class PricingModel {
  calculation: PricingModelType;
  public supplierName: string = LangPricingModel.supplier();

  constructor(calculation: PricingModelType, min?: number, max?: number) {
    this.calculation = calculation;
    this._min = min;
    this._max = max;
  }

  _min?: number | null;

  get min() {
    return this._min ?? 0;
  }

  _max?: number | null;

  get max() {
    switch (this.calculation) {
      case pricingModelType.margin:
        return this._max ?? 99;
      case pricingModelType.markup:
        return this._max ?? -1;
      default:
        throw new DevelopmentError(`Unknown pricing model type: ${this.calculation}`);
    }
  }

  get editorMin() {
    return this._min ?? 0;
  }

  get editorMax() {
    switch (this.calculation) {
      case pricingModelType.margin:
        return this._max ?? 99;
      case pricingModelType.markup:
        return (this._max ?? -1) === -1 ? undefined : (this._max ?? undefined);
    }
    return undefined;
  }

  public get calculationLabel() {
    return this.getCalculationTypeLabel(this.calculation);
  }

  get label() {
    return this.getPricingModelLabel(this.calculation, this.min, this.max);
  }

  public get pricingModelContstraintText() {
    return this.getPricingModelContstraintText(this.calculation, this.min, this.max);
  }

  public getCalculationTypeLabel(calculation: number) {
    switch (calculation) {
      case pricingModelType.margin:
        return LangPricingModel.margin();
      case pricingModelType.markup:
        return LangPricingModel.markup();
      default:
        return LangPricingModel.unknown();
    }
  }

  public getPricingModelContstraintText(calculation: PricingModelType, min?: number, max?: number) {
    switch (calculation) {
      case pricingModelType.markup:
        if (isNothing(min, max) || (min === 0 && max === -1)) return ``;
        if (isSomething(min) && (min ?? 0) > 0 && (isNothing(max) || max === -1)) return `(${min ?? 0}% <)`;
        return `(${min ?? 0}% - ${max ?? 2000}%)`;
      case pricingModelType.margin:
        return min || max ? `(${min ?? 0}% - ${max ?? 99}%)` : ``;
      default:
        throw new DevelopmentError(`Unknown pricing model type: ${calculation}`);
    }
  }

  public getPricingModelLabel(calculation: PricingModelType, min?: number, max?: number) {
    const calcType = this.getCalculationTypeLabel(calculation);

    switch (calculation) {
      case pricingModelType.markup:
        if (isNothing(min, max) || (min === 0 && max === -1)) return `${calcType} (%)`;
        if (isSomething(min) && (min ?? 0) > 0 && (isNothing(max) || max === -1)) return `${calcType} (${min ?? 0}% <)`;
        return `${calcType} (${min ?? 0}% - ${max ?? 2000}%)`;
      case pricingModelType.margin:
        return min || max ? `${calcType} (${min ?? 0}% - ${max ?? 99}%)` : `${calcType} (%)`;
      default:
        throw new DevelopmentError(`Unknown pricing model type: ${calculation}`);
    }
  }

  update(o: { calculation?: PricingModelType; min?: number | null; max?: number | null }) {
    this.calculation = o.calculation ?? this.calculation;
    this._min = o.min ?? null;
    this._max = o.max ?? null;
  }

  getRangeValidations(errors?: IErrorList): IErrorList {
    if (!errors) errors = errorList();
    switch (this.calculation) {
      case pricingModelType.margin:
        errors.assert(this.min >= 0, LangPricingModel.minMargin());
        errors.assert(this.min < this.max, LangPricingModel.maxMargin());
        errors.assert(this.max < 100, LangPricingModel.maxMargin100());
        break;
      case pricingModelType.markup:
        errors.assert(this.min >= 0, LangPricingModel.minMarkup());
        errors.assert(this.max === -1 || this.max >= this.min, LangPricingModel.maxMarkup());
        break;
      default:
        throw new DevelopmentError(`Unknown pricing model type: ${this.calculation}`);
    }
    return errors;
  }

  isValid(value: number) {
    return !this.getValueValidations(value).hasErrors;
  }

  getValueValidations(value: number, errors?: IErrorList, displayFieldName?: string): IErrorList {
    if (!errors) errors = errorList();
    const subRange =
      this.max === -1 ? LangPricingModel.minPercent(this.min) : LangPricingModel.minMaxPercent(this.min, this.max);

    const displayField = displayFieldName
      ? LangPricingModel.refPercentageMarginMarkupFieldWithName(displayFieldName)
      : LangPricingModel.refPercentageMarginMarkupField();
    switch (this.calculation) {
      case pricingModelType.margin:
        errors.assert(
          value >= this.min && value <= this.max,
          LangPricingModel.refSupplierMarginRestrictionMsg(this.supplierName, displayField, subRange)
        );
        break;
      case pricingModelType.markup:
        errors.assert(
          value >= this.min && (this.max === -1 || value <= this.max),
          LangPricingModel.refSupplierMarkupRestrictionMsg(this.supplierName, displayField, subRange)
        );
        break;
      default:
        throw new DevelopmentError(`Unknown pricing model type: ${this.calculation}`);
    }
    return errors;
  }
}
